import { useState, useEffect, useCallback } from "react";
import {
  BookOpen, Plus, Edit2, Trash2, Eye, EyeOff, Search,
  Loader2, Inbox, Save, X, Sprout, FileText,
} from "lucide-react";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { helpApi } from "@/lib/api";
import { toast } from "sonner";
import { TiptapEditor } from "@/components/TiptapEditor";

const PORTAL_OPTIONS = ["customer", "admin", "both"];
const EMPTY_FORM = {
  title: "", section: "", content: "",
  portal_type: "customer", feature_tag: "", sort_order: 0, is_published: true, language_code: "en",
};

// ── Read-only article viewer dialog ──────────────────────────────────────────
function ArticleViewer({ article, onClose }) {
  const html = DOMPurify.sanitize(article.content || "");
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 pt-10 overflow-y-auto" data-testid="article-viewer-dialog">
      <div className="bg-white w-full max-w-2xl shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b gap-4">
          <div>
            <h2 className="font-semibold text-slate-900 text-base leading-snug">{article.title}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-xs text-slate-400">{article.section}</span>
              <span className="text-xs text-slate-300">·</span>
              <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded border ${
                article.portal_type === "admin" ? "bg-purple-50 text-purple-700 border-purple-200"
                : article.portal_type === "both" ? "bg-cyan-50 text-cyan-700 border-cyan-200"
                : "bg-slate-100 text-slate-600 border-slate-200"
              }`}>{article.portal_type}</span>
              <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded ${
                article.is_published ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-600"
              }`}>{article.is_published ? "Published" : "Draft"}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors shrink-0 mt-0.5" data-testid="close-article-viewer">
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Content */}
        <div
          className="px-6 py-5 prose prose-sm max-w-none prose-headings:text-slate-800 prose-a:text-cyan-600 prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:text-cyan-700 prose-code:text-xs prose-pre:bg-slate-950 prose-pre:text-slate-200 prose-table:text-sm overflow-y-auto max-h-[60vh]"
          dangerouslySetInnerHTML={{ __html: html }}
          data-testid="article-viewer-content"
        />
        <div className="flex justify-end px-6 py-3 border-t bg-slate-50">
          <Button variant="outline" onClick={onClose} className="text-sm">Close</Button>
        </div>
      </div>
    </div>
  );
}

// ── Article form dialog ───────────────────────────────────────────────────────
function ArticleForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="article-form-dialog">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-slate-900">{initial?.article_id ? "Edit Article" : "New Article"}</h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="close-article-form">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1 block">Title *</label>
            <input value={form.title} onChange={(e) => set("title", e.target.value)}
              className="w-full border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-cyan-500" placeholder="Article title"
              data-testid="article-title-input" />
          </div>

          {/* Section + Portal + Feature tag row */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1 block">Section *</label>
              <input value={form.section} onChange={(e) => set("section", e.target.value)}
                className="w-full border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-cyan-500" placeholder="e.g. API Keys"
                data-testid="article-section-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1 block">Portal</label>
              <select value={form.portal_type} onChange={(e) => set("portal_type", e.target.value)}
                className="w-full border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-cyan-500 bg-white"
                data-testid="article-portal-select">
                {PORTAL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1 block">Feature Tag</label>
              <input value={form.feature_tag} onChange={(e) => set("feature_tag", e.target.value)}
                className="w-full border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-cyan-500" placeholder="e.g. api_keys"
                data-testid="article-tag-input" />
            </div>
          </div>

          {/* Sort order + Published */}
          <div className="flex items-center gap-6">
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1 block">Sort Order</label>
              <input type="number" value={form.sort_order} onChange={(e) => set("sort_order", Number(e.target.value))}
                className="w-24 border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-cyan-500"
                data-testid="article-sort-input" />
            </div>
            <div className="flex items-center gap-2 mt-4">
              <input type="checkbox" id="is_published" checked={form.is_published}
                onChange={(e) => set("is_published", e.target.checked)}
                className="w-4 h-4 accent-cyan-600" data-testid="article-published-checkbox" />
              <label htmlFor="is_published" className="text-sm text-slate-700">Published</label>
            </div>
          </div>

          {/* Content */}
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2 block">Content *</label>
            <TiptapEditor
              value={form.content}
              onChange={(html) => set("content", html)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-slate-50">
          <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={saving || !form.title || !form.section || !form.content}
            className="bg-cyan-600 hover:bg-cyan-500 text-white gap-2" data-testid="save-article-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save Article
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Article row ───────────────────────────────────────────────────────────────
function ArticleRow({ article, onEdit, onDelete, onToggle, onView }) {
  const portalColor = article.portal_type === "admin"
    ? "bg-purple-50 text-purple-700 border-purple-200"
    : article.portal_type === "both"
    ? "bg-cyan-50 text-cyan-700 border-cyan-200"
    : "bg-slate-100 text-slate-600 border-slate-200";

  return (
    <div className="grid grid-cols-[1fr_160px_100px_100px_120px] border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`article-row-${article.article_id}`}>
      <div className="px-4 py-3">
        <button
          onClick={() => onView(article)}
          className="text-sm font-medium text-slate-800 hover:text-cyan-600 transition-colors text-left"
          data-testid={`view-title-${article.article_id}`}
        >{article.title}</button>
        <div className="text-xs text-slate-400 font-mono mt-0.5">{article.feature_tag || "—"}</div>
      </div>
      <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{article.section}</div>
      <div className="px-4 py-3 flex items-center">
        <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium border rounded ${portalColor}`}>
          {article.portal_type}
        </span>
      </div>
      <div className="px-4 py-3 flex items-center">
        <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded ${article.is_published ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-600"}`}>
          {article.is_published ? "Published" : "Draft"}
        </span>
      </div>
      <div className="px-4 py-3 flex items-center gap-1">
        <button onClick={() => onView(article)} title="View article"
          className="p-1.5 text-slate-400 hover:text-cyan-600 transition-colors" data-testid={`view-article-${article.article_id}`}>
          <FileText className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onToggle(article)} title={article.is_published ? "Unpublish" : "Publish"}
          className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors" data-testid={`toggle-publish-${article.article_id}`}>
          {article.is_published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
        <button onClick={() => onEdit(article)} className="p-1.5 text-slate-400 hover:text-cyan-600 transition-colors"
          data-testid={`edit-article-${article.article_id}`}>
          <Edit2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(article)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
          data-testid={`delete-article-${article.article_id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HelpManage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);   // null | EMPTY_FORM | article
  const [viewing, setViewing] = useState(null);   // null | article
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [allArticles, setAllArticles] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await helpApi.adminListArticles({});
      const list = Array.isArray(data) ? data : [];
      setAllArticles(list);
      setArticles(list);
    } catch {
      setAllArticles([]);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter locally by title + feature_tag only (not content)
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) { setArticles(allArticles); return; }
    setArticles(allArticles.filter(a =>
      a.title.toLowerCase().includes(q) ||
      (a.feature_tag || "").toLowerCase().includes(q) ||
      a.section.toLowerCase().includes(q)
    ));
  }, [query, allArticles]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (form.article_id) {
        await helpApi.adminUpdate(form.article_id, form);
        toast.success("Article updated");
      } else {
        await helpApi.adminCreate(form);
        toast.success("Article created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.message || "Failed to save article");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    try {
      await helpApi.adminDelete(article.article_id);
      toast.success("Article deleted");
      load();
    } catch (e) {
      toast.error(e.message || "Failed to delete");
    }
  };

  const handleToggle = async (article) => {
    try {
      const r = await helpApi.adminTogglePublish(article.article_id);
      toast.success(r.is_published ? "Published" : "Unpublished");
      const updater = (prev) => prev.map((a) => a.article_id === article.article_id ? { ...a, is_published: r.is_published } : a);
      setArticles(updater);
      setAllArticles(updater);
    } catch (e) {
      toast.error(e.message || "Failed to toggle");
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const r = await helpApi.adminSeed();
      toast.success(r.message);
      load();
    } catch (e) {
      toast.error(e.message || "Failed to seed");
    } finally {
      setSeeding(false);
    }
  };

  const total = articles.length;
  const published = articles.filter((a) => a.is_published).length;

  return (
    <div className="p-6 space-y-6" data-testid="help-manage-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-cyan-600" />
          <div>
            <h1 className="text-xl font-bold text-slate-900">Help Content Manager</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {total} articles · {published} published
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {total === 0 && (
            <Button variant="outline" onClick={handleSeed} disabled={seeding}
              className="gap-2 text-sm" data-testid="seed-articles-btn">
              {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sprout className="w-3.5 h-3.5" />}
              Seed Default Articles
            </Button>
          )}
          <Button onClick={() => setEditing(EMPTY_FORM)}
            className="bg-cyan-600 hover:bg-cyan-500 text-white gap-2 text-sm" data-testid="new-article-btn">
            <Plus className="w-4 h-4" /> New Article
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search articles…"
          className="w-full pl-9 pr-4 py-2 border border-slate-200 text-sm focus:outline-none focus:border-cyan-500 bg-white"
          data-testid="admin-help-search" />
      </div>

        <div className="bg-white border border-slate-200 overflow-hidden" data-testid="articles-table">
          {/* Header */}
          <div className="grid grid-cols-[1fr_160px_100px_100px_120px] bg-slate-50 border-b border-slate-200">
            <div className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Title / Tag</div>
            <div className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Section</div>
            <div className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Portal</div>
            <div className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</div>
            <div className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : articles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-3" data-testid="help-manage-empty">
              <Inbox className="w-10 h-10" />
              <p className="text-sm">No articles found.</p>
              {total === 0 && (
                <p className="text-xs text-slate-400">Click <strong>Seed Default Articles</strong> to add 12 starter articles.</p>
              )}
            </div>
          ) : articles.map((a) => (
            <ArticleRow key={a.article_id} article={a}
              onView={setViewing} onEdit={setEditing} onDelete={handleDelete} onToggle={handleToggle} />
          ))}
        </div>

      {/* Article form modal */}
      {editing && (
        <ArticleForm
          initial={editing.article_id ? editing : null}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
          saving={saving}
        />
      )}

      {/* Article viewer modal */}
      {viewing && (
        <ArticleViewer
          article={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
